import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { Box, AppBar, Toolbar, Typography, IconButton, Tabs, Tab, Container, CircularProgress, Alert, Button, Tooltip } from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import RefreshIcon from '@mui/icons-material/Refresh';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { useAuth0 } from './context/Auth0Context';
import logger from '../utils/logger';

// Import MEMER components
import { MemerButton } from './ui/memer/MemerButton';
import { MemerH4 } from './ui/memer/MemerTypography';

// Import RPC monitoring components
import { RPCStatusIndicator, RPCHealthAlert, RPCHealthMonitor } from './RPCHealthMonitor';

// Import tab components
import TransactionBuilder from './transaction-builder/TransactionBuilder';
import FunctionExplorer from './transaction-builder/FunctionExplorer';
import TimelockManager from './timelock/TimelockManager';
import TransactionHistory from './history/TransactionHistory';
import SubgraphExplorer from './subgraph/SubgraphExplorer';
import FeeManagementDashboard from './dashboard/FeeManagementDashboard';
import StandaloneConverter from './utils/StandaloneConverter';
import FloatingConverter from './utils/FloatingConverter';
import WorkflowTemplateSelector from './workflow/WorkflowTemplateSelector';
import WorkflowExecutor from './workflow/WorkflowExecutor';

// Import database maintenance
// DatabaseMaintenancePanel archived - functionality moved to archived-functions

// Import dashboard
import ContractsBalancesDialog from './dashboard/ContractsBalancesDialog';

// Import user profile dialog
import UserProfileDialog from './auth/UserProfileDialog';

// Import context hooks
import { useWallet } from './context/WagmiWalletContext';
import { useContracts } from './context/ContractContext';
import { useDashboardData } from '../hooks/useDashboardData';

// Import Rainbow Kit connect button (for enhanced wallet UI)
import RainbowKitCustomConnectButton from './wallet/RainbowKitCustomConnectButton';

// Import Web3React wallet context (original working setup)
import { useWallet as useWeb3ReactWallet } from './context/WagmiWalletContext';

// Import hex converter hooks
import { useFloatingConverter, useConverterKeyboardShortcut } from '../hooks/useFloatingConverter';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

interface FunctionExplorerProps {
    workflowContext: {
        activeWorkflows: { type: string; key: string; timestamp: number }[];
        registerWorkflow: (type: string) => string;
        completeWorkflow: (key: string) => void;
    };
    // Add other props here if needed
}

// Control debug logging and features
const DEBUG = false;
const ENABLE_DEBUG_COMPONENT = true;
const FORCE_LOADING_COMPLETE_TIMEOUT = 15000; // 15 seconds max wait before forcing complete

const log = (...args: any[]) => {
    if (DEBUG) logger.log(...args);
};

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`tab-${index}`}
            aria-labelledby={`tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

// Emergency Recovery Component - moved outside to fix hooks order
const EmergencyRecovery: React.FC<{ onRecoveryAvailable: (available: boolean) => void }> = ({ onRecoveryAvailable }) => {
    const [localRecoveryAvailable, setLocalRecoveryAvailable] = useState(false);

    useEffect(() => {
        // Check for various signs of interrupted workflows
        const signatureState = localStorage.getItem('signature_state_before_collect');
        const activeSignatureFlow = localStorage.getItem('signature_flow_active') === 'true';

        const isRecoveryAvailable = !!signatureState || activeSignatureFlow;

        setLocalRecoveryAvailable(isRecoveryAvailable);
        onRecoveryAvailable(isRecoveryAvailable);
    }, [onRecoveryAvailable]);

    const handleRecover = () => {
        try {
            logger.log('🔄 Recovery initiated - restoring signature workflow state');

            // Dispatch recovery event for components to handle
            window.dispatchEvent(new CustomEvent('forceStateRecovery'));

            // Give components time to restore state
            setTimeout(() => {
                logger.log('✅ Recovery complete - state restored');
                setLocalRecoveryAvailable(false);
                onRecoveryAvailable(false);
            }, 500);
        } catch (error) {
            logger.error('❌ Recovery failed:', error);
        }
    };

    const handleDismiss = () => {
        try {
            logger.log('🧹 Dismissing recovery banner and clearing stale state');

            // Clear all signature-related localStorage items
            localStorage.removeItem('signature_state_before_collect');
            localStorage.removeItem('signature_flow_active');
            localStorage.removeItem('multisig_signatures');
            localStorage.removeItem('multisig_signature_progress');
            localStorage.removeItem('current_signature_flow_key');

            // Update state to hide banner
            setLocalRecoveryAvailable(false);
            onRecoveryAvailable(false);

            logger.log('✅ Recovery banner dismissed and state cleared');
        } catch (error) {
            logger.error('❌ Error dismissing recovery banner:', error);
        }
    };

    if (!localRecoveryAvailable) return null;

    return (
        <Alert
            severity="warning"
            sx={{ mb: 2 }}
            onClose={handleDismiss}
            action={
                <Button
                    color="inherit"
                    variant="outlined"
                    size="small"
                    onClick={handleRecover}
                >
                    Recover Workflow
                </Button>
            }
        >
            It appears a previous signature collection was interrupted. Click to recover or dismiss this message.
        </Alert>
    );
};

// Wallet State Debug Component - moved outside to fix hooks order
const WalletStateDebug: React.FC = () => {
    const { provider, signer, isConnected } = useWallet();
    const [signerAddress, setSignerAddress] = useState<string | null>(null);
    const prevSignerRef = useRef(signer);

    useEffect(() => {
        // Only update if signer has actually changed (reference equality)
        if (signer !== prevSignerRef.current) {
            prevSignerRef.current = signer;

            const updateSignerInfo = async () => {
                if (signer) {
                    try {
                        const address = await signer.getAddress();
                        setSignerAddress(address);
                    } catch (e) {
                        logger.error("Error getting signer address:", e);
                        setSignerAddress(null);
                    }
                } else {
                    setSignerAddress(null);
                }
            };

            updateSignerInfo();
        }
    }, [signer]);

    if (process.env.NODE_ENV !== 'development') return null;

    return (
        <Box sx={{ p: 2, border: '1px dashed grey', mt: 2, bgcolor: 'background.paper' }}>
            <Typography variant="caption">Wallet Debug</Typography>
            <Typography variant="body2">
                Connected: {isConnected ? '✅' : '❌'} |
                Provider: {provider ? '✅' : '❌'} |
                Signer: {signer ? '✅' : '❌'} |
                Address: {signerAddress || 'None'}
            </Typography>
        </Box>
    );
};

// Inner component that uses the context hooks
const MultiSigUIInner: React.FC = () => {
    // ALL STATE HOOKS FIRST - MUST BE CALLED ON EVERY RENDER
    const [activeTab, setActiveTab] = useState(0);
    const [isDashboardOpen, setIsDashboardOpen] = useState(false);
    const [isUserProfileOpen, setIsUserProfileOpen] = useState(false);
    const [connectionAttempted, setConnectionAttempted] = useState(false);
    const [showDebugInfo, setShowDebugInfo] = useState(false);
    const [loadingTimeout, setLoadingTimeout] = useState(false);
    const [recoveryAvailable, setRecoveryAvailable] = useState(false);
    const [executingWorkflow, setExecutingWorkflow] = useState<any>(null);

    // Track active workflows to restore on remount
    const [activeWorkflows, setActiveWorkflows] = useState<{
        type: string;
        key: string;
        timestamp: number;
    }[]>(() => {
        try {
            const saved = localStorage.getItem('active_workflows');
            return saved ? JSON.parse(saved) : [];
        } catch (e) {
            return [];
        }
    });

    // ALL CONTEXT HOOKS SECOND - MUST BE CALLED ON EVERY RENDER
    const { isAuthenticated, isLoading: auth0Loading, loginWithRedirect } = useAuth0();
    const { provider, isInitialized, initializationError, connectWallet } = useWallet();
    const { isLoading: contractsLoading, contracts, deploymentDetails, memerCoreAddress, error: contractsError, forceLoadingComplete } = useContracts();
    const { isOpen: isConverterOpen, initialValue, initialMode, openConverter, closeConverter } = useFloatingConverter();
    const handleKeyDown = useConverterKeyboardShortcut(openConverter);

    // ALL CALLBACK HOOKS THIRD - MUST BE CALLED ON EVERY RENDER
    const handleTabChange = useCallback((_event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    }, []);

    const registerWorkflow = useCallback((type: string) => {
        const key = `workflow_${type}_${Date.now()}`;
        setActiveWorkflows(prev => {
            const newWorkflows = [...prev, { type, key, timestamp: Date.now() }];
            localStorage.setItem('active_workflows', JSON.stringify(newWorkflows));
            return newWorkflows;
        });
        return key;
    }, []);

    const completeWorkflow = useCallback((key: string) => {
        setActiveWorkflows(prev => {
            const newWorkflows = prev.filter(w => w.key !== key);
            localStorage.setItem('active_workflows', JSON.stringify(newWorkflows));
            return newWorkflows;
        });
    }, []);

    const refreshData = useCallback(async () => {
        logger.log('Dashboard refresh disabled');
        return null;
    }, []);

    // ALL MEMO HOOKS FOURTH - MUST BE CALLED ON EVERY RENDER
    const workflowContext = useMemo(() => ({
        activeWorkflows,
        registerWorkflow,
        completeWorkflow
    }), [activeWorkflows, registerWorkflow, completeWorkflow]);

    // Mock dashboard data - temporarily disabled to fix infinite update loop
    const dashboardLoading = false;
    const multiSigBalances: { name: string; address: string; balance: string; type: 'multiSig' | 'contract' | 'token'; tokenBalances?: { token: string; balance: string; }[] }[] = [];
    const contractBalances: { name: string; address: string; balance: string; type: 'multiSig' | 'contract' | 'token'; tokenBalances?: { token: string; balance: string; }[] }[] = [];
    const dashboardError = null;
    const lastUpdated = null;
    const needsConnection = false;

    // ALL EFFECT HOOKS LAST - MUST BE CALLED ON EVERY RENDER
    // DEBUG: Log component state changes (reduced frequency)
    useEffect(() => {
        if (DEBUG) {
            console.log('🔍 MultiSigUIInner state:', {
                isAuthenticated,
                auth0Loading,
                activeTab,
                connectionAttempted,
                loadingTimeout
            });
        }
    }, [isAuthenticated, auth0Loading, activeTab, connectionAttempted, loadingTimeout]);

    // DEBUG: Log wallet state (only on significant changes)
    useEffect(() => {
        if (DEBUG && (isInitialized || initializationError)) {
            console.log('🔍 Wallet state:', {
                provider: !!provider,
                isInitialized,
                initializationError
            });
        }
    }, [provider, isInitialized, initializationError]);

    // DEBUG: Log contracts state (only when contracts change)
    useEffect(() => {
        if (DEBUG && contracts?.length > 0) {
            console.log('🔍 Contracts state:', {
                contractsLoading,
                contractsCount: contracts?.length || 0,
                deploymentDetails: !!deploymentDetails,
                memerCoreAddress,
                contractsError
            });
        }
    }, [contractsLoading, contracts, deploymentDetails, memerCoreAddress, contractsError]);

    // Component mount effect
    useEffect(() => {
        log("MultiSigUIContainer mounted - not auto-connecting wallet");
        setConnectionAttempted(true);
    }, []);

    // Force loading to complete after timeout
    useEffect(() => {
        if (contractsLoading) {
            const timer = setTimeout(() => {
                log("⏱️ Loading timeout reached, forcing completion");
                forceLoadingComplete();
                setLoadingTimeout(true);
            }, FORCE_LOADING_COMPLETE_TIMEOUT);
            return () => clearTimeout(timer);
        }
    }, [contractsLoading, forceLoadingComplete]);

    // Timeout effect for dashboard loading
    useEffect(() => {
        let loadingTimeout: NodeJS.Timeout;
        if (dashboardLoading) {
            loadingTimeout = setTimeout(() => {
                refreshData().catch((err: unknown) => {
                    logger.error('Error refreshing dashboard data after timeout:', err);
                });
            }, 10000);
        }
        return () => {
            if (loadingTimeout) clearTimeout(loadingTimeout);
        };
    }, [dashboardLoading, refreshData]);

    // Debug output for loading state changes
    useEffect(() => {
        // Commented out to reduce noise
    }, [dashboardLoading, multiSigBalances, contractBalances, dashboardError, lastUpdated]);

    // Add keyboard shortcut listener and tab switching event listener
    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        const handleTabSwitch = (event: CustomEvent) => {
            const { value, mode } = event.detail;
            setActiveTab(7);
            openConverter(value, mode);
        };
        window.addEventListener('switchToConverterTab', handleTabSwitch as EventListener);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('switchToConverterTab', handleTabSwitch as EventListener);
        };
    }, [handleKeyDown, openConverter]);

    // NOW ALL CONDITIONAL RENDERING LOGIC - NO MORE HOOKS AFTER THIS POINT

    // Show Auth0 loading state
    if (auth0Loading) {
        console.log('🔍 Rendering Auth0 loading state');
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                gap: 2
            }}>
                <CircularProgress size={40} />
                <Typography variant="h6">
                    Authenticating...
                </Typography>
            </Box>
        );
    }

    // Show login screen if not authenticated
    if (!isAuthenticated) {
        console.log('🔍 Rendering login screen - not authenticated');
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                gap: 3,
                backgroundColor: '#f5f5f5'
            }}>
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <img
                        src="/img/MemerLogo.png"
                        alt="MEMER Logo"
                        style={{ width: 80, height: 80, marginBottom: 16 }}
                    />
                    <MemerH4 sx={{ mb: 1, color: '#D4AF37' }}>
                        MEMER 么么儿
                    </MemerH4>
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
                        Operations Dashboard
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Secure access required for operational tools
                    </Typography>
                </Box>

                <Button
                    variant="contained"
                    size="large"
                    onClick={() => loginWithRedirect()}
                    sx={{
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        backgroundColor: '#D4AF37',
                        '&:hover': {
                            backgroundColor: '#B8941F'
                        }
                    }}
                >
                    Login to Access Dashboard
                </Button>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
                    Authorized personnel only
                </Typography>
            </Box>
        );
    }

    // Show error state if initialization failed
    if (initializationError) {
        console.log('🔍 Rendering initialization error:', initializationError);
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {initializationError}
                </Alert>
                <Button
                    variant="contained"
                    onClick={() => window.location.reload()}
                >
                    Retry Connection
                </Button>
            </Box>
        );
    }

    if (needsConnection) {
        console.log('🔍 Rendering wallet connection screen');
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                gap: 2
            }}>
                <Typography variant="h6">
                    Please connect your wallet
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => connectWallet('metamask')}
                    >
                        Connect MetaMask
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => connectWallet('coinbase')}
                    >
                        Connect Coinbase Wallet
                    </Button>
                </Box>
            </Box>
        );
    }

    // After the needsConnection check but before the contract loading check
    if (dashboardLoading && !contractsLoading) {
        console.log('🔍 Rendering dashboard loading screen');
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                gap: 2
            }}>
                <CircularProgress size={40} />
                <Typography variant="h6">
                    Loading balances...
                </Typography>
            </Box>
        );
    }

    // Show loading state while initializing - but with a more lenient condition
    // Only show loading if we have no contracts AND we're still loading AND no timeout
    if ((!isInitialized && !loadingTimeout) || (contractsLoading && contracts.length === 0 && !loadingTimeout)) {
        console.log('🔍 Rendering contracts loading screen:', {
            isInitialized,
            contractsLoading,
            contractsLength: contracts.length,
            loadingTimeout
        });
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                gap: 2
            }}>
                <CircularProgress size={40} />
                <Typography variant="h6">
                    {contractsLoading ? 'Loading contracts...' : 'Initializing application...'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {`Found ${contracts.length} contracts`}
                </Typography>

                {contractsError && (
                    <Alert severity="warning" sx={{ mt: 2, maxWidth: '80%' }}>
                        {contractsError}
                    </Alert>
                )}

                {/* Force Continue button */}
                <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => {
                        console.log('🔍 Force continue clicked');
                        forceLoadingComplete();
                        setLoadingTimeout(true);
                    }}
                    sx={{ mt: 2 }}
                >
                    Force Continue
                </Button>

                {/* Emergency bypass */}
                <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                        console.log('🔍 Emergency bypass clicked - proceeding with partial data');
                        setLoadingTimeout(true);
                    }}
                    sx={{ mt: 1, fontSize: '0.8rem' }}
                >
                    Continue with available data
                </Button>
            </Box>
        );
    }

    // DEBUG: Log successful render
    console.log('🔍 Rendering main UI successfully!', {
        isAuthenticated,
        isInitialized,
        contractsCount: contracts.length,
        activeTab
    });

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh'
        }}>
            {/* MEMER Branded Header - Fixed at top */}
            <AppBar position="fixed" elevation={0} sx={{ zIndex: 1100 }}>
                <Toolbar>
                    <Box display="flex" alignItems="center" flexGrow={1}>
                        <img
                            src="/img/MemerLogo.png"
                            alt="MEMER Logo"
                            style={{ width: 40, height: 40, marginRight: 12 }}
                        />
                        <MemerH4 sx={{ mb: 0, color: '#D4AF37' }}>
                            MEMER 么么儿
                        </MemerH4>
                        <Typography
                            variant="h6"
                            sx={{
                                ml: 3,
                                color: '#1A1A1A',
                                fontFamily: '"Noto Serif SC", serif'
                            }}
                        >
                            Operations Dashboard
                        </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={2}>
                        {/* RPC Status Indicator */}
                        <RPCStatusIndicator />

                        {/* Contracts & Balances Dashboard */}
                        <Tooltip title="Contracts & Balances">
                            <Button
                                variant="contained"
                                startIcon={<AccountBalanceWalletIcon />}
                                onClick={() => setIsDashboardOpen(true)}
                                sx={{
                                    backgroundColor: '#D4AF37',
                                    color: '#1A1A1A',
                                    fontWeight: 'bold',
                                    '&:hover': {
                                        backgroundColor: '#B8941F',
                                        color: '#FFFFFF'
                                    }
                                }}
                            >
                                Contracts & Balances
                            </Button>
                        </Tooltip>

                        {/* Rainbow Kit Connect Button */}
                        <RainbowKitCustomConnectButton />
                    </Box>

                    <UserProfileButton />
                    <LogoutButton />
                </Toolbar>
            </AppBar>

            {/* Fixed Tabs - positioned below the AppBar */}
            <Box sx={{
                position: 'fixed',
                top: 64, // Height of AppBar (Toolbar height)
                left: 0,
                right: 0,
                backgroundColor: 'background.paper',
                zIndex: 1099,
                borderBottom: 1,
                borderColor: 'divider'
            }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ minHeight: 48 }}
                >
                    <Tab label="Transaction Builder" />
                    <Tab label="Function Explorer" />
                    <Tab label="Workflows" />
                    <Tab label="Timelock Operations" />
                    <Tab label="Transaction History" />
                    <Tab label="Subgraph Explorer" />
                    <Tab label="System Health" />
                    <Tab label="DEX Management" />
                    <Tab label="Database Maintenance" />
                    <Tab label="Hex Converter" />
                </Tabs>
            </Box>

            {/* Spacer to account for fixed header and tabs */}
            <Box sx={{ height: 64 + 48 }} />

            {/* Emergency Recovery Banner - DISABLED for production cleanup */}
            {/* <EmergencyRecovery onRecoveryAvailable={setRecoveryAvailable} /> */}

            <Container maxWidth="xl" sx={{ flexGrow: 1, pb: 4 }}>
                {/* RPC Health Alert - shows when there are provider issues */}
                <RPCHealthAlert />

                {loadingTimeout && (
                    <Alert
                        severity="warning"
                        sx={{ mb: 3 }}
                        onClose={() => setLoadingTimeout(false)}
                    >
                        Loading was force-completed due to timeout. Some functionality may be limited.
                    </Alert>
                )}

                <TabPanel value={activeTab} index={0}>
                    <TransactionBuilder workflowContext={workflowContext} />
                </TabPanel>

                <TabPanel value={activeTab} index={1}>
                    <FunctionExplorer workflowContext={workflowContext} />
                </TabPanel>

                <TabPanel value={activeTab} index={2}>
                    {executingWorkflow ? (
                        <WorkflowExecutor
                            template={executingWorkflow}
                            workflowContext={workflowContext}
                            onComplete={(results) => {
                                logger.log('Workflow completed with results:', results);
                                setExecutingWorkflow(null);
                                // Show success message or handle results
                            }}
                            onCancel={() => {
                                logger.log('Workflow cancelled');
                                setExecutingWorkflow(null);
                            }}
                        />
                    ) : (
                        <WorkflowTemplateSelector
                            onSelectTemplate={(template) => {
                                logger.log('Selected workflow template:', template.name);
                                setExecutingWorkflow(template);
                            }}
                        />
                    )}
                </TabPanel>

                <TabPanel value={activeTab} index={3}>
                    <TimelockManager />
                </TabPanel>

                <TabPanel value={activeTab} index={4}>
                    <TransactionHistory />
                </TabPanel>

                <TabPanel value={activeTab} index={5}>
                    <SubgraphExplorer />
                </TabPanel>

                <TabPanel value={activeTab} index={6}>
                    <Box>
                        <Typography variant="h4" gutterBottom>
                            System Health Dashboard
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                            Monitor RPC provider health and system performance for the MEMER ecosystem
                        </Typography>

                        <RPCHealthMonitor showDetails={true} />
                    </Box>
                </TabPanel>

                <TabPanel value={activeTab} index={7}>
                    <Box>
                        <Typography variant="h4" gutterBottom>
                            DEX Fee Management
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                            Monitor and manage fees from Uniswap V3 and Aerodrome V2 liquidity positions
                        </Typography>

                        {/* Uniswap V3 Fee Management */}
                        <Box sx={{ mb: 4 }}>
                            <FeeManagementDashboard
                                dexType="uniswap"
                                contractAddress={deploymentDetails?.liquidity?.uniswapSetup?.addressForUI}
                            />
                        </Box>

                        {/* Aerodrome V2 Fee Management */}
                        <Box>
                            <FeeManagementDashboard
                                dexType="aerodrome"
                                contractAddress={deploymentDetails?.liquidity?.aerodromeSetup?.addressForUI}
                            />
                        </Box>
                    </Box>
                </TabPanel>

                <TabPanel value={activeTab} index={8}>
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Database Maintenance functionality has been archived to reduce serverless function count for Vercel deployment.
                        </Alert>
                        <Typography variant="h6" color="text.secondary">
                            Database Maintenance Panel Archived
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            This functionality is available in the archived components if needed for development.
                        </Typography>
                    </Box>
                </TabPanel>

                <TabPanel value={activeTab} index={9}>
                    <StandaloneConverter />
                </TabPanel>
            </Container>

            {/* Only render the dialog when it's open to prevent unnecessary initialization */}
            {isDashboardOpen && (
                <ContractsBalancesDialog
                    open={isDashboardOpen}
                    onClose={() => setIsDashboardOpen(false)}
                />
            )}

            {/* Floating Converter - Global Access */}
            <FloatingConverter
                open={isConverterOpen}
                onClose={closeConverter}
                initialValue={initialValue}
                initialMode={initialMode}
            />
        </Box>
    );
};

// User profile button component
const UserProfileButton: React.FC = () => {
    const [isUserProfileOpen, setIsUserProfileOpen] = useState(false);

    return (
        <>
            <Tooltip title="User Profile & Settings">
                <IconButton
                    color="inherit"
                    onClick={() => setIsUserProfileOpen(true)}
                    sx={{ mr: 1 }}
                >
                    <AccountCircleIcon />
                </IconButton>
            </Tooltip>

            <UserProfileDialog
                open={isUserProfileOpen}
                onClose={() => setIsUserProfileOpen(false)}
            />
        </>
    );
};

// Logout button component
const LogoutButton: React.FC = () => {
    const { logout } = useAuth0();

    return (
        <IconButton
            color="inherit"
            onClick={() => logout(true)}
            title="Logout"
        >
            <LogoutIcon />
        </IconButton>
    );
};

// Main container component without additional context providers
// (providers should be in App.tsx or MyApp.tsx)
const MultiSigUIContainer: React.FC = () => {
    return <MultiSigUIInner />;
};

export default MultiSigUIContainer;
